import * as React from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { Box, Button, Card, FormControl, InputLabel, MenuItem, Select, Typography } from '@material-ui/core';
import { CircularProgress } from '@pec/aion-ui-components/components/CircularProgress';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { ESTIMATE_ONE, PartnerNames } from '../../constants/dataSharingConsent';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { PARTNER_LOGOS } from '../../helpers/partner';
import { postDataSharingConsent } from '../../slices/dataSharingConsent';
import { renderHTMLString } from '../../helpers/helpers';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useTypedSelector } from '../../combineReducers';

type RouteParams = {
  registrationGUID: string;
  companyName: string;
  partnerId: string;
};

const useStyles = makeStyles(theme =>
  createStyles({
    container: {
      padding: theme.spacing(2, 2),
      margin: theme.spacing(2, 2)
    },

    header: {
      display: 'flex',
      justifyContent: 'left',
      alignItems: 'center',
      paddingLeft: theme.spacing(1),
      marginTop: theme.spacing(1)
    },
    card: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      '& > *': {
        marginBottom: theme.spacing(2)
      }
    },
    agreeButton: {
      color: '#FFF',
      maxHeight: '5px'
    }
  })
);

type dataSharingType = {
  companyName: string;
  companyOrganizationId: string;
  title: string;
  logo: string;
  textCardLeft: string;
  textCardRight: string;
};

/**
 * PARTNERID = ESTIMATEONE
 * COMPANY NAME = CONTRACTOR COMPANY NAME
 * ID = REGISTRATION CODE
 */

export const PartnerPage: React.FC = () => {
  const classes = useStyles();
  const configUrl = `/organizations/static/configs/gb-dataSharingConsent.json`;
  const dispatch = useDispatch();
  const { partnerId } = useParams<RouteParams>(); // Third party name i.e - EstimateOne
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const registrationGUID = searchParams.get('id'); // registration code
  const companyName = searchParams.get('name'); // contractor company name
  const callbackUrl = searchParams.get('redirectUrl');
  const { allUserOrganizations } = useTypedSelector(state => state.userOrganizations);
  const { organization } = useTypedSelector(state => state.organization);
  const [userOrganizationList, setUserOrganizationList] = React.useState<any[]>([]);
  const { isFetching } = useTypedSelector(state => state.dataSharingConsent);
  const [selectedOrganization, setSelectedOrganization] = React.useState(organization || null);
  const isMounted = React.useRef(true);
  const [loading, setLoading] = React.useState(false);
  const [checked, setChecked] = React.useState<boolean>(false);
  const { userInfo } = useTypedSelector(state => state.userInfo);
  const [dataSharingConfig, setDataSharingConfig] = React.useState<dataSharingType>({
    companyName: '',
    companyOrganizationId: '',
    title: '',
    logo: '',
    textCardLeft: '',
    textCardRight: ''
  });

  const partnerNameToIdMap = new Map<string, string>([[PartnerNames.EstimateOne, ESTIMATE_ONE]]);

  React.useEffect(() => {
    isMounted.current = true;
    if (allUserOrganizations.length > 0 || organization) {
      const uniqueOrganizations = Array.from(
        new Map(
          [...(organization ? [organization] : []), ...allUserOrganizations].map(org => [
            org.name.trim().toLowerCase(),
            org
          ])
        ).values()
      );
      setUserOrganizationList(uniqueOrganizations);
      if (!selectedOrganization && organization) {
        const matchedOrg = uniqueOrganizations.find(
          org => org.name.trim().toLowerCase() === companyName?.trim().toLowerCase()
        );
        setSelectedOrganization((matchedOrg || organization) as any);
      }
    }
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName, organization, selectedOrganization]);

  React.useEffect(() => {
    fetch(configUrl)
      .then(response => response.json())
      .then(config => {
        const currentConfig = config['thirdPartyDataConnections'];
        if (currentConfig) {
          const activeConfig = currentConfig.find(
            (el: any) => el.companyOrganizationId === partnerNameToIdMap.get(partnerId)
          );
          setDataSharingConfig(activeConfig as dataSharingType);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Leave empty, ignore the ESLint warning.

  React.useEffect(() => {
    isFetching ? setLoading(true) : setLoading(false);
  }, [isFetching]);

  const authoriseDataSharing = () => {
    if (selectedOrganization && userInfo) {
      const payload = {
        cproOrganizationId: selectedOrganization.id,
        thirdPartyOrganizationId: partnerNameToIdMap.get(partnerId),
        isDataSharing: true,
        shouldRedirect: true,
        externalRegistrationId: registrationGUID,
        userId: userInfo.userId,
        name: userInfo.fullName,
        email: userInfo.emailAddress,
        phone: userInfo.phoneNumber,
        callbackUrl: callbackUrl || null
      };

      dispatch(postDataSharingConsent(payload));
    }
  };

  const Logo = PARTNER_LOGOS[dataSharingConfig.logo];

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setChecked(checked);
  };

  return (
    <>
      <Box className={classes.container}>
        <ResponsiveMasonry columnsCountBreakPoints={{ 320: 1, 1280: 2 }}>
          <Masonry gutter={'40px'}>
            <Card className={classes.card}>
              <Box>
                <Box>{Logo ? <Logo /> : null}</Box>
                <Typography variant="h6">{dataSharingConfig.companyName}</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                RECOMMENDED
              </Typography>
              <Typography variant="h5" color="textSecondary">
                {dataSharingConfig.title}
              </Typography>
              <Typography
                variant="body1"
                dangerouslySetInnerHTML={{
                  __html: renderHTMLString(dataSharingConfig.textCardLeft)
                }}
              />
            </Card>
            <Box>
              <br />
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                <div>
                  <Typography variant="h6" color="textSecondary" style={{ paddingBottom: '20px' }}>
                    To continue your registration and to share your company data with {dataSharingConfig.companyName},
                    please review the following statement:
                  </Typography>
                </div>

                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', paddingBottom: '20px' }}>
                  <Checkbox onChange={handleCheckboxChange} checked={checked} style={{ paddingLeft: '0px' }} />
                  <div>
                    {' '}
                    I authorise {dataSharingConfig.companyName} to use my name and contact detail in their applications
                    and services. I am authorised by '{selectedOrganization?.name}' to share their company and
                    membership data with {dataSharingConfig.companyName} for the remainder of the CHAS membership
                    period. I understand I can remove my consent at any time."
                  </div>
                </div>
              </div>

              <Typography component="h1">
                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-label">Organization</InputLabel>
                  <Select
                    value={selectedOrganization?.id || ''}
                    onChange={event => {
                      if (!isMounted.current) return;
                      const selectedOrg = userOrganizationList.find(org => org.id === event.target.value);
                      setSelectedOrganization(selectedOrg || null);
                    }}
                  >
                    {userOrganizationList.length > 0 ? (
                      userOrganizationList.map(organization => (
                        <MenuItem key={organization?.id} value={organization?.id}>
                          {organization?.name}
                        </MenuItem>
                      ))
                    ) : (
                      <Loading />
                    )}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <Button
                    disabled={!checked}
                    onClick={authoriseDataSharing}
                    variant={'contained'}
                    color={'primary'}
                    style={{ marginBottom: '20px' }}
                  >
                    {loading ? (
                      <CircularProgress className={classes.agreeButton} color="primary" size={24} />
                    ) : (
                      'LINK MY CHAS ACCOUNT'
                    )}
                  </Button>
                  <Typography variant="body1">
                    Once your account is linked you will be redirected to E1 to complete your registration
                  </Typography>
                </FormControl>
              </Typography>
            </Box>
          </Masonry>
        </ResponsiveMasonry>
      </Box>
    </>
  );
};
